// IMPORTANT: After modifying this file, update CHANGELOG.md with a summary of your changes.
// Add clear comments for non-obvious changes, including what replaced prior behavior, to avoid repeating failed fixes.

"use client";

import { useState } from "react";

/**
 * Click-to-play YouTube player (#103). Until the visitor clicks, only the
 * static `img.youtube.com` thumbnail loads: no iframe and no YouTube script,
 * so the player costs nothing on mobile page speed. The click swaps in a
 * privacy-enhanced `youtube-nocookie.com` iframe with autoplay (the click
 * is the user gesture that allows it). A plain iframe, not the IFrame JS API.
 *
 * The CSP `frame-src` in next.config.ts allows youtube-nocookie.com for this.
 * `videoId` is restricted to YouTube's id alphabet by the block schema.
 */
export function YouTubeFacade({
  videoId,
  caption,
}: {
  videoId: string;
  caption: string;
}) {
  const [playing, setPlaying] = useState(false);
  const title = caption || "YouTube video";

  if (playing) {
    return (
      <iframe
        src={`https://www.youtube-nocookie.com/embed/${videoId}?autoplay=1`}
        title={title}
        allow="autoplay; encrypted-media; picture-in-picture; fullscreen"
        allowFullScreen
        className="absolute inset-0 h-full w-full"
      />
    );
  }

  return (
    <button
      type="button"
      onClick={() => setPlaying(true)}
      aria-label={`Play video: ${title}`}
      className="group absolute inset-0 flex h-full w-full cursor-pointer flex-col items-center justify-center gap-6 bg-stone-900 text-white"
    >
      {/* Decorative: the button's aria-label names the video. */}
      <img
        src={`https://img.youtube.com/vi/${videoId}/hqdefault.jpg`}
        alt=""
        loading="lazy"
        decoding="async"
        className="absolute inset-0 h-full w-full object-cover opacity-40"
      />
      <span
        aria-hidden="true"
        className="relative flex h-16 w-16 items-center justify-center rounded-full border-[3px] border-opichi-green-light transition-transform group-hover:scale-110"
      >
        <svg
          viewBox="0 0 24 24"
          className="ml-1 h-7 w-7 fill-opichi-green-light"
        >
          <path d="M6 4l14 8-14 8z" />
        </svg>
      </span>
      {caption && (
        <span className="relative max-w-xs px-6 text-center text-sm uppercase tracking-[0.2em]">
          {caption}
        </span>
      )}
    </button>
  );
}
